import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  TableContainer,
  LinearProgress,
  InputAdornment,
  TableSortLabel,
  MenuItem,
} from '@mui/material';
import { Delete, SearchRounded, VisibilityOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getCvApplications, getCvJobs, deleteCvApplication } from '../../../../services/api';
import ApplicationDetails from './ApplicationDetails';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { useLocation, useNavigate } from 'react-router-dom';

const statuses = [
  { v: '', label: 'Todos' },
  { v: 'in_progress', label: 'Em andamento', color: 'info' },
  { v: 'passed', label: 'Aprovados', color: 'success' },
  { v: 'rejected', label: 'Reprovados', color: 'error' },
  { v: 'hired', label: 'Contratados', color: 'success' },
];

const statusMap = {
  ...Object.fromEntries(statuses.map((s) => [s.v, s])),
  pending_adjust: { v: 'in_progress', label: 'Em andamento', color: 'warning' },
  manual: { v: 'in_progress', label: 'Em andamento', color: 'warning' },
};

const SORTABLE_COLUMNS = [
  { key: 'candidate_name', label: 'Candidato', minWidth: 260 },
  { key: 'job_title', label: 'Vaga', minWidth: 220 },
  { key: 'status', label: 'Status', minWidth: 180 },
  { key: 'created_at', label: 'Data da aplicação', minWidth: 190 },
];

const normalize = (value) => String(value || '').toLowerCase().trim();

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
};

const getStatusLabel = (value) => statusMap[value]?.label || value || 'Sem status';

export default function Applications() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [jobId, setJobId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');

  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const data = await getCvApplications(params);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getCvJobs();
        if (!active) return;
        setJobs(Array.isArray(data) ? data : []);
      } catch {
        if (!active) return;
        setJobs([]);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status') || '';
    const jobParam = params.get('jobId') || params.get('job_id') || '';
    setStatus(statusParam);
    setJobId(jobParam);
    void load({ status: statusParam || undefined, jobId: jobParam || undefined });
  }, [location.search, load]);

  const updateQuery = (nextStatus, nextJob) => {
    const params = new URLSearchParams();
    if (nextStatus) params.set('status', nextStatus);
    if (nextJob) params.set('jobId', nextJob);
    const query = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: query ? `?${query}` : '',
      },
      { replace: true },
    );
  };

  const handleDelete = async () => {
    try {
      await deleteCvApplication(deleteId);
      toast.success('Candidatura removida com sucesso!');
      await load({ status: status || undefined, jobId: jobId || undefined });
    } catch {
      toast.error('Erro ao remover a candidatura.');
    } finally {
      setDeleteId(null);
    }
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    updateQuery(value, jobId);
  };

  const handleJobChange = (value) => {
    setJobId(value);
    updateQuery(status, value);
  };

  const getComparableValue = useCallback((row, column) => {
    if (column === 'candidate_name') return normalize(row?.candidate_name);
    if (column === 'job_title') return normalize(row?.job_title);
    if (column === 'status') return normalize(getStatusLabel(row?.status));

    if (column === 'created_at') {
      const timestamp = new Date(row?.created_at || '').getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    return '';
  }, []);

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalize(search);
    if (!normalizedSearch) return rows;

    return rows.filter((row) => {
      const candidate = normalize(row?.candidate_name);
      const job = normalize(row?.job_title);
      const statusLabel = normalize(getStatusLabel(row?.status));
      const dateLabel = normalize(formatDateTime(row?.created_at));

      return (
        candidate.includes(normalizedSearch)
        || job.includes(normalizedSearch)
        || statusLabel.includes(normalizedSearch)
        || dateLabel.includes(normalizedSearch)
      );
    });
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      const valueA = getComparableValue(a, orderBy);
      const valueB = getComparableValue(b, orderBy);

      let result = 0;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = valueA - valueB;
      } else {
        result = String(valueA).localeCompare(String(valueB), 'pt-BR', { sensitivity: 'base' });
      }

      return orderDirection === 'asc' ? result : -result;
    });

    return sorted;
  }, [filteredRows, getComparableValue, orderBy, orderDirection]);

  const handleRequestSort = (column) => {
    if (orderBy === column) {
      setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setOrderBy(column);
    setOrderDirection(column === 'created_at' ? 'desc' : 'asc');
  };

  if (selectedAppId) {
    return <ApplicationDetails applicationId={selectedAppId} onBack={() => setSelectedAppId(null)} />;
  }

  const hasActiveFilter = Boolean(status || jobId || search.trim());

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          background: 'linear-gradient(135deg, #f8fbff 0%, #f2f7ff 100%)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Gerenciamento de Candidaturas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Acompanhe candidaturas, aplique filtros e acesse os detalhes com rapidez.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${rows.length} ${rows.length === 1 ? 'candidatura' : 'candidaturas'}`}
                />
                <Chip
                  size="small"
                  color={hasActiveFilter ? 'info' : 'default'}
                  variant="outlined"
                  label={`${sortedRows.length} exibidas`}
                />
              </Stack>
            </Box>
          </Stack>

          <Grid container spacing={1.25}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(event) => handleStatusChange(event.target.value)}
                fullWidth
              >
                {statuses.map((item) => (
                  <MenuItem key={item.v} value={item.v}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                size="small"
                label="Vaga"
                value={jobId}
                onChange={(event) => handleJobChange(event.target.value)}
                fullWidth
              >
                <MenuItem value="">Todas</MenuItem>
                {jobs.map((job) => (
                  <MenuItem key={job.id} value={job.id}>
                    {job.title || `Vaga ${job.id}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                fullWidth
                placeholder="Buscar por candidato, vaga, status ou data"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {loading && <LinearProgress />}

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                '& th:first-of-type, & td:first-of-type': {
                  pl: 3,
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {SORTABLE_COLUMNS.map((column) => (
                    <TableCell key={column.key} sx={{ minWidth: column.minWidth, fontWeight: 700 }}>
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? orderDirection : 'asc'}
                        onClick={() => handleRequestSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 130, fontWeight: 700 }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelectedAppId(row.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack spacing={0.2}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.candidate_name || 'Candidato sem nome'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.job_title || 'Vaga não informada'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={getStatusLabel(row.status)}
                        color={statusMap[row.status]?.color || 'default'}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(row.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedAppId(row.id);
                          }}
                          aria-label="Ver detalhes"
                        >
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir candidatura">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteId(row.id);
                          }}
                          aria-label="Excluir candidatura"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && sortedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography sx={{ py: 3 }} color="text.secondary">
                        {search ? 'Nenhuma candidatura encontrada para sua busca.' : 'Nenhuma candidatura encontrada.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.25 }}>
          <Stack spacing={1.25}>
            {sortedRows.map((row) => (
              <Paper
                key={`mobile-${row.id}`}
                variant="outlined"
                onClick={() => setSelectedAppId(row.id)}
                sx={{ p: 1.5, borderRadius: 1.5, cursor: 'pointer' }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.candidate_name || 'Candidato sem nome'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.job_title || 'Vaga não informada'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={getStatusLabel(row.status)}
                      color={statusMap[row.status]?.color || 'default'}
                    />
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Data da aplicação: {formatDateTime(row.created_at)}
                  </Typography>

                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Ver detalhes">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedAppId(row.id);
                        }}
                        aria-label="Ver detalhes"
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Excluir candidatura">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteId(row.id);
                        }}
                        aria-label="Excluir candidatura"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            ))}

            {!loading && sortedRows.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {search ? 'Nenhuma candidatura encontrada para sua busca.' : 'Nenhuma candidatura encontrada.'}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Paper>

      <ConfirmDialog
        open={Boolean(deleteId)}
        title="Excluir candidatura"
        message="Tem certeza que deseja excluir esta candidatura? Todas as respostas associadas serão perdidas."
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
}
